import FormLabel from '@c/forms/controls/FormLabel';
import { LoginCountryDropdown } from '@c/forms/CountryDropdown';
import { zodResolver } from '@hookform/resolvers/zod';
import { completeRegistration, logEvent } from '@util/analytics';
import { AUTH } from '@util/firebase';
import { createUserDocument, getUserById } from '@util/firestore/users';
import { getValidUserDocument } from '@util/firestore/users/user.helpers';
import { logError } from '@util/logError';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { dialCountries } from 'features/contryDropdown/lib/countries';
import PhoneOTP from 'features/phoneOTP';
import { usePhoneOTP } from 'features/phoneOTP/hooks/usePhoneOTP';
import { FirebaseError } from 'firebase/app';
import { getAdditionalUserInfo } from 'firebase/auth';
import { motion } from 'framer-motion';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import { CountryCode } from 'libphonenumber-js/types';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '..';

function PhoneForm({
  phone: initialPhone,
  verificationId: initialVerificationId,
  continueFn,
  title = 'Enter Phone Number',
  countryCode = 'US',
  setCountryCode,
  setCreatedUserDoc,
  attribution,
  existingPhoneUser,
}: {
  phone: string;
  verificationId: string;
  continueFn: () => void;
  title?: string;
  countryCode?: string;
  setCountryCode?: (code: string) => void;
  setCreatedUserDoc?: Dispatch<
    SetStateAction<{
      uid: string;
      name: string;
      isSocial: boolean;
      phone: string;
      smsConsent: boolean;
    }>
  >;
  existingPhoneUser?: {
    phone: string;
    uid: string;
  };
  attribution?:
    | {
        source: string;
        medium: string | null;
        campaign: string | null;
        term: string | null;
        content: string | null;
        id: string | null;
        fbclid: string | null;
      }
    | null
    | undefined;
}) {
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState(initialPhone);
  const [verificationId, setVerificationId] = useState(initialVerificationId);
  const {
    linkPhone,
    sendPhoneVerification,
    signInWithPhone,
    user,
    userDoc,
    refreshUserDoc,
  } = useAuth();
  const { showSuccessToast, showErrorToast } = useToastContext();
  const [sendingCode, setSendingCode] = useState(false);
  const [localCountryCode, setLocalCountryCode] = useState(countryCode);
  const [authError, setAuthError] = useState<Error | null>(null);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const savedCountryCode = localStorage.getItem('mxlocker_country_code');
    if (savedCountryCode) {
      setLocalCountryCode(savedCountryCode);
      if (setCountryCode) {
        setCountryCode(savedCountryCode);
      }
    }
  }, [setCountryCode]);

  const handleCountryCodeChange = (code: string) => {
    localStorage.setItem('mxlocker_country_code', code);
    setLocalCountryCode(code);
    if (setCountryCode) {
      setCountryCode(code);
    }
  };

  const form = useForm<{ code: string }>({
    defaultValues: {
      code: '',
    },
    mode: 'onBlur',
    resolver: zodResolver(
      z.object({ code: z.string().length(6, 'Code is six digits') })
    ),
  });

  const { otpDigits, setOtpDigits, timeToResend, setTimeToResend } =
    usePhoneOTP();

  const formatPhoneNumber = (phoneNumber: string): string => {
    return phoneNumber.replace(/[\s-]/g, '');
  };

  async function onSubmit(form: { code: string }, retry = false) {
    if (searchParams?.get('step')) {
      if (AUTH.currentUser) {
        setLoading(true);

        try {
          await linkPhone(verificationId, form.code);
          continueFn();
        } catch (error) {
          const message = (error as Error).message;
          const isPhoneAlreadyInUse =
            message === 'Phone number already in use' ||
            (error instanceof FirebaseError &&
              (error.code === 'auth/credential-already-in-use' ||
                error.code ===
                  'auth/account-exists-with-different-credential'));

          if (isPhoneAlreadyInUse) {
            if (
              pathname?.startsWith('/order-confirmation') &&
              user?.isAnonymous
            ) {
              try {
                const res = await signInWithPhone(verificationId, form.code);
                if ((res as any)._tokenResponse) {
                  router.push(
                    `/link-order?&it=${(res as any)._tokenResponse.idToken}`
                  );
                } else {
                  setAuthError(
                    new Error('An error occurred. Please try again.')
                  );
                }
              } catch (error) {
                if (
                  (error as Error).message.includes(
                    'Cloud function deadline exceeded'
                  )
                ) {
                  setTimeout(async () => {
                    const cred = await signInWithPhone(
                      verificationId,
                      form.code
                    );
                    router.push(
                      `/link-order?&it=${(cred as any)._tokenResponse.idToken}`
                    );
                  });
                } else {
                  logError(error);
                  setAuthError(
                    new Error('An error occurred. Please try again.')
                  );
                }
              }
              return;
            }
            setAuthError(new Error('Phone number already in use'));
          } else if (error instanceof FirebaseError) {
            setAuthError(error as Error);
          } else if (
            message === 'Invalid verification code' ||
            message === 'Missing verification code' ||
            message === 'Verification code expired'
          ) {
            setAuthError(error as Error);
          } else {
            logError(error);
            setAuthError(new Error('An error occurred. Please try again.'));
          }
        } finally {
          setLoading(false);
        }
      }
    } else {
      try {
        setLoading(true);
        if (
          pathname === '/verify-phone' ||
          pathname?.startsWith('/order-confirmation')
        ) {
          if (!AUTH.currentUser) {
            router.push('/login');
          } else {
            if (existingPhoneUser) {
              try {
                const res = await signInWithPhone(verificationId, form.code);
                if ((res as any)._tokenResponse) {
                  router.push(
                    `/link-order?&it=${(res as any)._tokenResponse.idToken}`
                  );
                  return;
                } else {
                  setAuthError(
                    new Error('An error occurred. Please try again.')
                  );
                  return;
                }
              } catch (error) {
                if (
                  (error as Error).message.includes(
                    'Cloud function deadline exceeded'
                  )
                ) {
                  setTimeout(async () => {
                    const res = await signInWithPhone(
                      verificationId,
                      form.code
                    );
                    router.push(
                      `/link-order?&it=${(res as any)._tokenResponse.idToken}`
                    );
                  });
                } else {
                  logError(error);
                  setAuthError(error as Error);
                }
                return;
              }
            } else {
              const res = await linkPhone(verificationId, form.code);
              if (res instanceof FirebaseError) {
                setAuthError(res);
                return;
              }
              if (user?.isAnonymous) {
                logEvent('guest_account_linked', {}, user.uid);
                // we don't care about awaiting result and it has its own error handling
                void completeRegistration(
                  user.uid,
                  'phone',
                  userDoc?.email || user.email || ''
                );
              }
            }
          }
        } else {
          const res = await signInWithPhone(verificationId, form.code);
          if (res instanceof FirebaseError) throw res;
          const additionalUserInfo = getAdditionalUserInfo(res);
          const isNewUser = additionalUserInfo?.isNewUser ?? false;
          if (isNewUser) {
            const validUserDoc = getValidUserDocument(
              res.user.uid,
              res.user.phoneNumber ?? '',
              {
                name: res.user.displayName?.split(' ')?.[0] ?? '',
                opted_out: false,
              },
              attribution ?? null
            );
            try {
              await createUserDocument(validUserDoc, 'phone');
              refreshUserDoc();
            } catch (e) {
              // this actually isn't a big deal b/c userDoc is created in backend authTrigger
              logError(e, 'createUserDocument');
            }
          } else {
            try {
              const directDoc = await getUserById(res.user.uid);
              if (directDoc?.email) {
                window.location.href = '/dashboard/my-profile';
                return;
              }
            } catch (e) {
              logError(e, 'getUserById');
            }
          }
          const createdUserDoc = {
            uid: res.user.uid,
            name: res.user.displayName?.split(' ')?.[0] ?? '',
            isSocial: false,
            phone: res.user.phoneNumber ?? '',
            isPhoneVerified: true,
            smsConsent: false,
          };

          setCreatedUserDoc?.(createdUserDoc);
        }

        showSuccessToast('Phone number verified!');

        continueFn();
      } catch (e) {
        if (e instanceof FirebaseError) {
          if (e.code === 'auth/invalid-verification-code') {
            setAuthError(new Error('Invalid verification code'));
            setLoading(false);
            setOtpDigits(Array(6).fill(''));
          } else if (
            e.code === 'auth/account-exists-with-different-credential'
          ) {
            setAuthError(new Error('Phone number already in use.'));
            setLoading(false);
          } else if (
            e.message.includes('Missing or insufficient permissions') ||
            e.message.includes('Cloud function deadline exceeded')
          ) {
            // retry
            if (retry) {
              setAuthError(new Error('An error occurred. Please try again.'));
              setLoading(false);
              return;
            } else {
              setAuthError(null);
              setTimeout(() => {
                onSubmit(form, true);
              }, 1000);
              return;
            }
          } else {
            setAuthError(e);
          }

          if (e.code === 'auth/phone')
            showErrorToast(e.message, 'bottom-center');
          logError(e, `verify-phone onSubmit - ${user?.uid || ''}`);
        } else {
          // TODO: remove this after we have proper error handling
          setAuthError(new Error(e as string));
          showErrorToast((e as Error).message, 'bottom-center');
          logError(e, `verify-phone onSubmit - ${user?.uid || ''}`);
        }
      } finally {
        setLoading(false);
      }
    }
  }

  const countryDialCode = dialCountries.find(
    (c) => c.code === localCountryCode
  )?.dial_code;

  const displayPhoneNumber = phone.startsWith('+')
    ? phone
    : `${countryDialCode}${phone}`;

  const sendCode = () => {
    let phoneWithCountry = formatPhoneNumber(phone);
    if (!phoneWithCountry.startsWith('+')) {
      phoneWithCountry = `+${countryDialCode?.replace(
        '+',
        ''
      )}${phoneWithCountry}`;
    }
    const parsed = parsePhoneNumberFromString(
      phoneWithCountry,
      localCountryCode as CountryCode
    );
    if (!parsed) {
      alert('Unable to parse phone number. Please try again.');
      return;
    }
    sendPhoneVerification(parsed.format('E.164'))
      .then((verificationId) => {
        form.setValue('code', '');
        setVerificationId(verificationId);
      })
      .catch((e) => {
        logError(e, `verify-phone resend - ${user?.uid}`);
        setAuthError(
          new Error(
            'Unable to send verification code. Please refresh the page and try again or contact support at support@mxlocker.com.'
          )
        );
      });
  };

  return (
    <div className="mx-auto flex w-full max-w-[36rem] flex-col gap-[1.6rem] sm:max-w-none">
      <div className="relative gap-[2rem]">
        <form
          className="flex flex-col gap-[4rem]"
          onSubmit={form.handleSubmit((form) => onSubmit(form, false))}
        >
          {phone && verificationId ? (
            <>
              <div className="flex flex-col gap-y-2">
                <h1 className="text-[3rem] font-semibold">
                  Enter your verification code
                </h1>
                <div className="flex items-center gap-x-4">
                  <h2 className="text-[1.8rem] text-[#444444]">
                    SMS code was sent to your phone{' '}
                    {formatPhoneNumber(displayPhoneNumber)}
                  </h2>
                  <button
                    onClick={() => {
                      const countryDialCode = dialCountries.find(
                        (c) => c.code === localCountryCode
                      )?.dial_code;
                      const phoneWithoutDialCode = phone.startsWith(
                        countryDialCode || ''
                      )
                        ? phone.slice(countryDialCode?.length)
                        : phone;

                      // removing the dial code from the phone number, since it will be set in the country selector dropdown
                      setPhone(phoneWithoutDialCode);
                      setVerificationId('');
                      setLoading(false);
                    }}
                    className="text-[1.8rem] font-semibold text-brand-secondary hover:text-brand-primary"
                  >
                    Edit
                  </button>
                </div>

                {authError && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="mt-8 flex flex-col"
                  >
                    <span className="tracking-widest text-zinc-500">ERROR</span>

                    <span className="max-w-[40rem] text-[1.8rem] font-medium text-brand-secondary">
                      {authError.message}
                    </span>
                  </motion.div>
                )}
              </div>

              <PhoneOTP
                codeOtpLoading={loading}
                otpDigits={otpDigits}
                setOtpDigits={setOtpDigits}
                submitConfirmationCode={(code) =>
                  onSubmit({ code: code.join('') })
                }
                timeToResend={timeToResend}
                setTimeToResend={setTimeToResend}
                sendConfirmationCode={sendCode}
                sendingCode={sendingCode}
              />
            </>
          ) : (
            <>
              <h1 className="text-[2rem] font-semibold sm:text-[3rem]">
                {title}
              </h1>

              <FormLabel required value="Phone Number">
                <LoginCountryDropdown
                  value={phone}
                  autoComplete="tel-national"
                  onChange={(e) => setPhone(e)}
                  onCountryChange={(code) => handleCountryCodeChange(code)}
                  countryCode={localCountryCode}
                />
              </FormLabel>
              <h2 className="text-[1.6rem]">
                We will send a verification code to this number
              </h2>
              <Button
                text="Send Code"
                loading={loading}
                type="secondary"
                className="!text-[1.8rem]"
                onClick={() => {
                  let formattedPhone = formatPhoneNumber(phone);
                  if (!formattedPhone.startsWith('+')) {
                    formattedPhone = `+${countryDialCode?.replace(
                      '+',
                      ''
                    )}${formattedPhone}`;
                  }
                  const parsed = parsePhoneNumberFromString(
                    formattedPhone,
                    localCountryCode as CountryCode
                  );

                  if (!parsed) {
                    alert('Unable to parse phone number. Please try again.');
                    return;
                  }
                  setLoading(true);
                  sendPhoneVerification(parsed.format('E.164'))
                    .then((verificationId) => {
                      setVerificationId(verificationId);
                    })
                    .catch((e) => {
                      if ((e as Error).message.includes('TOO_SHORT')) {
                        setAuthError(
                          new Error(
                            'Phone number is too short. Please try again.'
                          )
                        );
                        return;
                      }
                      if ((e as Error).message.includes('ALREADY_IN_USE')) {
                        setAuthError(
                          new Error(
                            'Phone number is already in use. Please log in instead.'
                          )
                        );
                        return;
                      }
                      logError(e, `verify-phone sendCode - ${user?.uid}`);
                      setAuthError(
                        new Error(
                          'Unable to send verification code. Please refresh the page and try again or contact support at support@mxlocker.com.'
                        )
                      );
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                }}
              />
            </>
          )}
        </form>
      </div>
    </div>
  );
}

export default PhoneForm;
